import { Injectable } from '@angular/core';
import { CacheManagementService } from './cache-management.service';
import { cloneDeep } from 'lodash/fp';
import {
  ModuleLicenceCodes,
  AuthActionCodes,
  EntityCodes,
} from '@core/models/global-entities/global-enums';
import {
  MultipleRightsCheck,
  PropertyBasicInfo,
  StaffBasicInfo,
} from '@core/models/global-entities/global-entities';
import { Params } from '@angular/router';
import { keyBy } from 'lodash/fp';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  constructor(public cacheService: CacheManagementService) {}

  GetProperties_Action(Action: string): Array<PropertyBasicInfo> {
    const lstProps = cloneDeep(this.cacheService.LoggedInUser.Properties) || [];
    const lstAuth = this.cacheService.LoggedInUser.PropertyAuthorizations || [];
    const lstLicence = this.cacheService.LoggedInUser.ActiveLicences || [];

    return lstProps.filter((p) => {
      return (
        lstLicence.findIndex(
          (a) =>
            a.PropertyId === p.PropertyId &&
            a.MODULE_CODE === ModuleLicenceCodes.KMTCore
        ) >= 0 &&
        lstAuth.findIndex(
          (a) =>
            a.PropertyId === p.PropertyId &&
            a.IsAllowed === true &&
            a.ActionCode === Action
        ) >= 0
      );
    });
  }

  GetProperties_ModuleLicence() {
    const lstLicence = this.cacheService.LoggedInUser.ActiveLicences || [];
    const lstProps = cloneDeep(this.cacheService.LoggedInUser.Properties || []);

    return lstProps.filter((p) => {
      return (
        lstLicence.findIndex(
          (a) =>
            a.PropertyId === p.PropertyId &&
            a.MODULE_CODE === ModuleLicenceCodes.KMTCore
        ) >= 0
      );
    });
  }

  CheckAuth_Action(Action: string): boolean {
    const lstAuth = this.cacheService.LoggedInUser.PropertyAuthorizations || [];
    // const userRight = lstAuth.find(a => a.ActionCode === Action);
    const licensedProps = this.GetProperties_ModuleLicence();
    if (licensedProps.length > 0) {
      if (
        lstAuth.findIndex(
          (a) =>
            licensedProps.some((p) => p.PropertyId === a.PropertyId) &&
            a.ActionCode === Action &&
            a.IsAllowed === true
        ) >= 0
      ) {
        return true;
      }
    }
    return false;
  }

  CheckAuth_MultiAction(
    queryParams: Params,
    actions?: MultipleRightsCheck[]
  ): boolean {
    actions = actions || [];
    const passed = actions.filter(
      (action) =>
        action.checker &&
        action.checker(queryParams) &&
        action.actionCode &&
        this.CheckAuth_Action(action.actionCode)
    );

    return passed.length > 0;
  }

  CheckAuth_Property_Action(PropertyId: number, Action: string): boolean {
    if (this.CheckActiveLicence_Action(PropertyId, Action)) {
      const lstAuth =
        this.cacheService.LoggedInUser.PropertyAuthorizations || [];
      if (
        lstAuth.findIndex(
          (a) =>
            a.IsAllowed === true &&
            a.ActionCode === Action &&
            a.PropertyId === PropertyId
        ) >= 0
      ) {
        return true;
      }
    }
    return false;
  }

  CheckActiveLicence_Action(PropertyId: number, Action: string) {
    const lstAuth = this.cacheService.LoggedInUser.PropertyAuthorizations || [];
    const lstLicence = this.cacheService.LoggedInUser.ActiveLicences || [];
    const userRight = lstAuth.find((a) => a.ActionCode === Action);
    if (
      userRight &&
      lstLicence.findIndex(
        (a) =>
          a.PropertyId === PropertyId && a.RoleModule === userRight.ModuleCode
      ) >= 0
    ) {
      return true;
    }
    return false;
  }

  CheckActiveLicence_Action_Department(
    PropertyId: number,
    Action: string,
    DepartmentId: number
  ) {
    const lstAuth = this.cacheService.LoggedInUser.PropertyAuthorizations || [];
    const lstLicence = this.cacheService.LoggedInUser.ActiveLicences || [];
    const userRight = lstAuth.find((a) => {
      return (
        a.ActionCode === Action &&
        a.DeptId == DepartmentId &&
        a.PropertyId == PropertyId
      );
    });
    if (
      userRight &&
      lstLicence.findIndex(
        (a) =>
          a.PropertyId === PropertyId && a.RoleModule === userRight.ModuleCode
      ) >= 0
    ) {
      return true;
    }
    return false;
  }

  CheckActiveLicence_Module(PropertyId: number, Module: string) {
    const lstLicence = this.cacheService.LoggedInUser.ActiveLicences || [];
    if (
      lstLicence.findIndex(
        (a) => a.PropertyId === PropertyId && a.MODULE_CODE === Module
      ) >= 0
    ) {
      return true;
    } else {
      return false;
    }
  }

  CheckAuth_EntityCode(entityCode: EntityCodes) {
    let ActionList: AuthActionCodes[] = [];
    switch (entityCode) {
      case EntityCodes.WorkOrder:
        ActionList = [
          AuthActionCodes.WorkOrder_Add,
          AuthActionCodes.WorkOrder_Console_View,
          AuthActionCodes.WorkOrder_Action,
        ];
        return this.CheckAuth_ActionList(ActionList);

      case EntityCodes.PM:
        ActionList = [AuthActionCodes.PM_Add, AuthActionCodes.PM_Console_View];
        return this.CheckAuth_ActionList(ActionList);

      case EntityCodes.CheckList:
        ActionList = [
          AuthActionCodes.Checklist_Add,
          AuthActionCodes.Checklist_Edit,
          AuthActionCodes.Checklist_Console_View,
        ];
        return this.CheckAuth_ActionList(ActionList);

      case EntityCodes.Asset:
        ActionList = [
          AuthActionCodes.Asset_Add,
          AuthActionCodes.Asset_Edit,
          AuthActionCodes.Asset_Delete,
          AuthActionCodes.Asset_Console_View,
        ];
        return this.CheckAuth_ActionList(ActionList);
    }
  }

  CheckAuth_ActionList(actionList: AuthActionCodes[]): boolean {
    const lstAuth = this.cacheService.LoggedInUser.PropertyAuthorizations || [];
    // const userRight = lstAuth.find(a => a.ActionCode === Action);
    const licenceProps = this.GetProperties_ModuleLicence();
    if (licenceProps.length > 0) {
      if (
        lstAuth.findIndex(
          (a) =>
            licenceProps.some((p) => p.PropertyId === a.PropertyId) &&
            actionList.some((x) => x === a.ActionCode) &&
            a.IsAllowed === true
        ) >= 0
      ) {
        return true;
      }
    }
    return false;
  }

  IsPropertyAdmin(): boolean {
    return this.cacheService.LoggedInUser.IsPropertyAdmin;
  }

  IsAdminUser(): boolean {
    return this.cacheService.LoggedInUser.IsAdminUser;
  }

  getPropertyAuthorizations(property: number[]) {
    const lstAuth = this.cacheService.LoggedInUser.PropertyAuthorizations || [];
    return property
      ? lstAuth.filter((prop) => {
          return property.find((propNumber) => {
            return prop.PropertyId === propNumber;
          });
        })
      : [];
  }

  getPropertyAuthorizationsByModule(property: number[], moduleCode: string) {
    let allPropertyAuthorizations = this.getPropertyAuthorizations(property);
    if (allPropertyAuthorizations && allPropertyAuthorizations.length) {
      return moduleCode
        ? allPropertyAuthorizations.filter(
            (prop) => prop.ModuleCode === moduleCode && prop.IsAllowed
          )
        : [];
    } else {
      return [];
    }
  }

  getDepartmentsByModuleAuthorizations(
    moduleCode: string,
    selectedPropertyIds?: number[],
    actionCode?: string
  ): number[] {
    const lstAuth = this.cacheService.LoggedInUser.PropertyAuthorizations || [];
    const moduleAuthorizations = lstAuth.filter(
      (auth) =>
        auth.IsAllowed &&
        auth.ModuleCode == moduleCode &&
        (selectedPropertyIds
          ? selectedPropertyIds.indexOf(auth.PropertyId) >= 0
          : true) &&
        (actionCode ? auth.ActionCode === actionCode : true)
    );
    const lstDepartments = keyBy('DeptId')(moduleAuthorizations);
    return Object.keys(lstDepartments).map(Number);
  }

  getDepartmentsByPropertyAuthorizations(
    property: number[],
    moduleCode: string
  ) {
    let authorizedProperties = this.getPropertyAuthorizationsByModule(
      property,
      moduleCode
    );
    let keyby_array = keyBy('DeptId')(authorizedProperties);
    return Object.keys(keyby_array).map(Number);
  }
}
